import React, { useState, useEffect } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { FileUploader } from "@capps/laddition-storybook";
import { Localized } from "fluent-react/compat";

import CallApi from "../../../../utils/CallApi";

const ImageUploader = ({ restaurantId, token, onImageUpdate }) => {
  const [uploadFiles, setUploadFiles] = useState([]);
  const [customImage, setCustomImage] = useState(null);
  const randomizeImg = Date.now();
  const [hasError, setHasError] = useState(false);
  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  const fetchCustomImage = async () => {
    try {
      const url = `restaurant/info`;
      const result = await CallApi("GET", url, restaurantId, null, token);

      if (result && result[0]?.resultRestaurant?.images?.length) {
        const customImg = result[0].resultRestaurant.images.find(
          (img) => img.image.name.startsWith("tr-image-custom-name")
        );
        setCustomImage({
          name: customImg.image.name,
          value: `${customImg.image.value}?v=${randomizeImg}`,
        });
      }
    } catch (e) {
      console.error("Erreur lors de la récupération de l'image :", e);
    }
  };

  const handleFileUpload = async () => {
    if (uploadFiles.length) {
      const file = uploadFiles[0].blob;
      const formData = new FormData();
      formData.append("file", file);

      const fakeLoading = new Promise((resolve) => setTimeout(resolve, 1500));

      await toast.promise(
        Promise.all([
          fakeLoading,
          (async () => {
            const response = await axios({
              method: "POST",
              data: formData,
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: token,
              },
              url: `/api/restaurant/admin/upload/${restaurantId}`,
            });

            if (!response.data.success) {
              throw new Error("Upload failed");
            }
            const formattedImage = {
              name: `tr-image-custom-name-${randomizeImg}`,
              value: response.data.result,
            };

            await CallApi("PUT", "restaurant/admin/info", restaurantId, {
              update: "imgToDisplay",
              images: [{image: formattedImage}],
            }, token);

            setCustomImage(formattedImage);
            setUploadFiles([]);
            if (onImageUpdate) onImageUpdate(formattedImage);
          })(),
        ]),
        {
          loading: <Localized id="tr-upload-loading"/>,
          success: <Localized id="tr-upload-success"/>,
          error: <Localized id="tr-upload-error"/>,
        },
        {
          style: {
            background: "#e9e9ee",
            color: "#000",
          },
          success: {
            style: {
              background: "#047857",
              color: "#FFF",
            },
          },
          error: {
            style: {
              background: "#ab2221",
              color: "#FFF",
            },
          },
        }
      );
    }
  };

  const handleDeleteImage = async () => {
    if (!customImage) return;

    const updatedImage = {
      ...customImage,
      name: "",
      value: "",
    };

    try {
      await CallApi(
        "PUT",
        "restaurant/admin/info",
        restaurantId,
        {
          update: "imgToDisplay",
          images: [{ image: updatedImage }],
        },
        token
      );

      setCustomImage(null);

      toast.success(<Localized id="tr-delete-success" />);
    } catch (e) {
      console.error("Erreur lors de la suppression de l'image :", e);
      toast.error(<Localized id="tr-delete-error" />);
    }
  };

  const handleFileValidation = (files) => {
    if (files.length > 0) {
      const file = files[0];
      const isValid = file.type.startsWith("image/") && file.size <= MAX_FILE_SIZE;
      setHasError(!isValid);
    } else {
      setHasError(true);
    }
    setUploadFiles(files);
  };

  const inputLabel = () => {
    return (
      <div className="sb-mx-auto sb-my-3 dark:sb-text-white">
        <Localized id="tr-admin-fileUploader-label1" /> <u className="sb-text-primary dark:sb-text-white"><Localized id="tr-admin-fileUploader-label2" /></u> <Localized id="tr-admin-fileUploader-label3" />
      </div>
    );
  };

  useEffect(() => {
    fetchCustomImage();
  }, [restaurantId, token]);

  return (
    <div className="container-upload-image">
      <div className="file-uploader-container">
        <FileUploader
            id="file-uploader"
            errorMessage={<Localized id="tr-error-invalid-image" />}
            uploadedFiles={uploadFiles}
            setData={handleFileValidation}
            zoneLabel={inputLabel()}
            inputProps={{
              accept: ".png, .jpg, .jpeg, .webp",
              multiple: false,
            }}
            restriction={[
              { fileType: "image", maxSize: MAX_FILE_SIZE },
            ]}
        />
        {customImage ? (
            <img
                src={customImage.value}
                alt={customImage.name}
                className="uploaded-image"
            />
        ) : (
            <div className="placeholder-image-container">
              <div className="placeholder-image">
                <Localized id="tr-placeholder-image"/>
              </div>
            </div>
        )}
      </div>
      <div className="btns-container">
        <button
          className="delete-button"
          onClick={handleDeleteImage}
          disabled={!customImage}
        >
          <Localized id="tr-admin-card-upload-delete" />
        </button>
        <button
          className="upload-button"
          onClick={handleFileUpload}
          disabled={!uploadFiles.length || hasError}
        >
          <Localized id="tr-link-button-upload" />
        </button>
      </div>
    </div>
  );
};

export default ImageUploader;
